import * as React from 'react'
import { Dialog as AriaDialog, Heading } from 'react-aria-components'
import { cn } from '../../lib/utils'
import { Button, type ButtonProps } from './button'
import { CloseButton } from './close-button'

/**
 * Dialog — a modal panel for forms, details, or actions. It's the panel +
 * content slots; the trigger, state, and backdrop come from the `Overlay`
 * primitives, so a complete dialog is `OverlayWrapper` → `Backdrop` → `Dialog`:
 *
 *   <OverlayWrapper trigger={<Button>Edit</Button>}>
 *     <Backdrop className="max-w-lg">
 *       <Dialog>
 *         <DialogHeader>
 *           <DialogTitle>Edit profile</DialogTitle>
 *           <DialogDescription>Update your details.</DialogDescription>
 *         </DialogHeader>
 *         <DialogBody>…</DialogBody>
 *         <DialogFooter>
 *           <DialogClose appearance="outline">Cancel</DialogClose>
 *           <Button>Save</Button>
 *         </DialogFooter>
 *       </Dialog>
 *     </Backdrop>
 *   </OverlayWrapper>
 *
 * Wraps react-aria's `Dialog` (focus trap + return, `aria-labelledby` from
 * `DialogTitle`). The panel is a flex column capped at `max-h-[85vh]`, so a
 * `DialogBody` scrolls while the header and footer stay put (sticky footer). A
 * top-right close button shows by default (`showCloseButton={false}` to hide).
 * `children` may be a function receiving `{ close }` for programmatic dismissal;
 * `DialogClose` (or any descendant) can also close via context.
 */

/**
 * Visual chrome shared by every modal panel (`Dialog`, `AlertDialog`): white
 * surface, gray-200 border, large shadow, 12px radius. Layout (padding, flex,
 * max-height) is added per-component so the family stays visually identical
 * without duplicating the token classes.
 */
export const dialogPanelClass =
  'rounded-xl border border-fbui-gray-200 bg-fbui-base-white shadow-fbui-lg outline-none'

// Exported so sibling modal panels (e.g. `Sheet`) can provide the same context
// and reuse `DialogClose` / `DialogDescription` unchanged.
//
// Note: this context is shared, not per-panel-type. If a `Dialog` and a `Sheet`
// are ever nested (e.g. a Dialog opened from inside Sidebar's mobile Sheet),
// `useDialogClose` / `DialogClose` resolve to the *nearest* provider — which is
// the inner panel, usually what you want. If a descendant ever needs to dismiss a
// specific outer panel across a nesting boundary, introduce a distinct context.
export const DialogCloseContext = React.createContext<(() => void) | null>(null)

/** Close the enclosing `Dialog` (or `Sheet`) from any descendant. */
export const useDialogClose = (): (() => void) => {
  const close = React.useContext(DialogCloseContext)
  if (!close) {
    throw new Error('useDialogClose must be used within a <Dialog> or <Sheet>')
  }
  return close
}

/**
 * Lets a `DialogDescription` adopt the panel's description id and toggle the
 * panel's `aria-describedby` while it's mounted — RAC only auto-wires the title,
 * not the description. The id is generated once on the panel; the description
 * reports its presence so the attribute only points at a node that exists.
 */
export const DialogDescriptionContext = React.createContext<{
  descriptionId: string
  setHasDescription: (present: boolean) => void
} | null>(null)

/**
 * Shared `aria-describedby` wiring for modal panels (`Dialog`, `Sheet`). Returns
 * the `describedBy` value to spread onto the panel and the `descriptionCtx` to
 * provide so a descendant `DialogDescription` can register itself.
 */
export const useModalDescription = (): {
  describedBy: string | undefined
  descriptionCtx: {
    descriptionId: string
    setHasDescription: (present: boolean) => void
  }
} => {
  const descriptionId = React.useId()
  const [hasDescription, setHasDescription] = React.useState(false)
  // Stable identity so the description's registration ref doesn't re-fire each render.
  const descriptionCtx = React.useMemo(
    () => ({ descriptionId, setHasDescription }),
    [descriptionId]
  )
  return {
    describedBy: hasDescription ? descriptionId : undefined,
    descriptionCtx,
  }
}

export interface DialogProps {
  /** Dialog content; a function form receives `{ close }`. */
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode)
  /** Show the default top-right close button. */
  showCloseButton?: boolean
  /** `dialog` (default) or `alertdialog`. */
  role?: 'dialog' | 'alertdialog'
  /** Classes for the dialog panel. */
  className?: string
  'aria-label'?: string
}

export const Dialog = ({
  children,
  showCloseButton = true,
  role = 'dialog',
  className,
  ...props
}: DialogProps) => {
  const { describedBy, descriptionCtx } = useModalDescription()
  return (
    <AriaDialog
      role={role}
      aria-describedby={describedBy}
      className={cn(
        'relative flex max-h-[85vh] flex-col overflow-hidden',
        dialogPanelClass,
        className
      )}
      {...props}
    >
      {({ close }) => (
        <DialogCloseContext.Provider value={close}>
          <DialogDescriptionContext.Provider value={descriptionCtx}>
            {typeof children === 'function' ? children({ close }) : children}
            {/* Rendered last (it's absolutely positioned) so react-aria's initial
                focus lands on the first meaningful control, not the ✕. */}
            {showCloseButton && (
              <CloseButton
                onClick={close}
                className="absolute right-4 top-4 z-10"
              />
            )}
          </DialogDescriptionContext.Provider>
        </DialogCloseContext.Provider>
      )}
    </AriaDialog>
  )
}
Dialog.displayName = 'Dialog'

/** Title + description block at the top of the dialog. */
export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-1.5 px-6 pr-12 pt-6', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

export interface DialogTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level (1–6) for the document outline. */
  level?: 1 | 2 | 3 | 4 | 5 | 6
}

/** The dialog title — labels the dialog for assistive tech via `slot="title"`. */
export const DialogTitle = ({
  className,
  level = 3,
  ...props
}: DialogTitleProps) => (
  <Heading
    slot="title"
    level={level}
    className={cn('text-base font-semibold text-fbui-gray-900', className)}
    {...props}
  />
)
DialogTitle.displayName = 'DialogTitle'

/** Supplementary description under the title. */
export const DialogDescription = ({
  className,
  id,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => {
  const ctx = React.useContext(DialogDescriptionContext)
  const fallbackId = React.useId()
  // Adopt the dialog's id + report presence (so it gets aria-describedby), unless
  // the consumer wired an explicit id (AlertDialog does — it owns both ends).
  const resolvedId = id ?? ctx?.descriptionId ?? fallbackId
  const registerRef = React.useCallback(
    (node: HTMLParagraphElement | null) => {
      if (id == null) ctx?.setHasDescription(node != null)
    },
    [ctx, id]
  )
  return (
    <p
      ref={registerRef}
      id={resolvedId}
      className={cn('text-sm text-fbui-text-body', className)}
      {...props}
    />
  )
}
DialogDescription.displayName = 'DialogDescription'

/** Scrollable main content area between the header and footer. */
export const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('fbui-scroll min-h-0 flex-1 overflow-y-auto px-6 py-[1rem]', className)}
    {...props}
  />
)
DialogBody.displayName = 'DialogBody'

/** Action bar at the bottom (stays put while the body scrolls). */
export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex justify-end gap-2 px-6 pb-6 pt-[1rem]', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

/** A `Button` that closes the enclosing dialog (accepts all `Button` props). */
export const DialogClose = ({
  onClick,
  children = 'Close',
  type,
  ...props
}: ButtonProps) => {
  const close = useDialogClose()
  return (
    <Button
      // Explicit so a DialogClose inside a <form> closes instead of submitting.
      type={type ?? 'button'}
      onClick={(e) => {
        onClick?.(e)
        close()
      }}
      {...props}
    >
      {children}
    </Button>
  )
}
DialogClose.displayName = 'DialogClose'
